import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Calendar as CalendarIcon,
  RefreshCw,
  Loader2,
  Clock,
  MapPin,
  ExternalLink,
  AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIAS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

function parseEventStart(ev) {
  return new Date(ev.inicio);
}

function fmtHour(d) {
  return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
}

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function AgendaView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetched, setLastFetched] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/agenda/events?days_back=2&days_forward=60`);
      setEvents(res.data || []);
      setLastFetched(new Date());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível carregar a agenda.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { groupedByDay, todayEvents, nextEvent } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // De-dup eventos (o feed iCal por vezes traz instâncias recorrentes duplicadas)
    const seen = new Set();
    const unique = [];
    for (const ev of events) {
      const start = parseEventStart(ev);
      const key = `${ev.uid}-${start.toISOString()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push({ ...ev, _start: start });
    }
    unique.sort((a, b) => a._start - b._start);

    const upcoming = unique.filter((ev) => ev._start >= today);
    const groups = new Map();
    for (const ev of upcoming) {
      const k = dayKey(ev._start);
      if (!groups.has(k)) groups.set(k, { date: new Date(ev._start), items: [] });
      groups.get(k).items.push(ev);
    }
    const todayItems = (groups.get(dayKey(today))?.items) || [];
    const next = unique.find((ev) => ev._start >= now) || null;

    return {
      groupedByDay: Array.from(groups.values()),
      todayEvents: todayItems,
      nextEvent: next,
    };
  }, [events]);

  return (
    <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16" data-testid="agenda-view">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-10 bg-yellow-400" />
            <span className="overline">Google Calendar · pedro.pando@gmail.com</span>
          </div>
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight leading-tight">
            <CalendarIcon className="inline mr-3 text-yellow-400" size={32} />
            Agenda de Treinos
          </h2>
          {lastFetched ? (
            <p className="text-xs text-neutral-500 mt-2">
              Última atualização: {lastFetched.toLocaleTimeString("pt-PT")}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <a
            href="https://calendar.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-xs inline-flex items-center gap-2"
            data-testid="agenda-open-google"
          >
            <ExternalLink size={14} /> Abrir no Google
          </a>
          <button
            onClick={load}
            className="btn-ghost text-xs inline-flex items-center gap-2"
            data-testid="agenda-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
        </div>
      </div>

      {error ? (
        <div
          className="card p-5 border-yellow-400/40 bg-yellow-400/5 flex items-start gap-3 mb-6"
          data-testid="agenda-error"
        >
          <AlertCircle className="text-yellow-400 shrink-0 mt-0.5" size={20} />
          <div>
            <div className="text-sm text-yellow-300 font-bold mb-1">Erro ao carregar a agenda</div>
            <div className="text-sm text-neutral-300">{error}</div>
          </div>
        </div>
      ) : null}

      {loading && events.length === 0 ? (
        <div className="card p-10 text-center text-neutral-400">
          <Loader2 className="animate-spin mx-auto mb-2" />
          A carregar agenda do Google...
        </div>
      ) : !error && events.length === 0 ? (
        <div className="card p-10 text-center text-neutral-500" data-testid="agenda-empty">
          Nenhum evento próximo na agenda.
        </div>
      ) : (
        <>
          {/* Destaques no topo */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="card p-5 border-yellow-400/30 bg-yellow-400/5">
              <div className="overline mb-2 text-yellow-400">Hoje</div>
              {todayEvents.length === 0 ? (
                <div className="text-sm text-neutral-400">Nenhum treino hoje.</div>
              ) : (
                <>
                  <div className="text-3xl font-display tracking-tight">
                    {todayEvents.length}
                    <span className="text-base text-neutral-400 font-sans normal-case ml-2">
                      {todayEvents.length === 1 ? "sessão marcada" : "sessões marcadas"}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-2">
                    Primeira: {fmtHour(todayEvents[0]._start)} · {todayEvents[0].titulo}
                  </div>
                </>
              )}
            </div>
            <div className="card p-5">
              <div className="overline mb-2">Próxima sessão</div>
              {nextEvent ? (
                <>
                  <div className="font-display text-xl tracking-tight uppercase leading-tight">
                    {nextEvent.titulo}
                  </div>
                  <div className="text-sm text-yellow-400 mt-1">
                    {DIAS[nextEvent._start.getDay()]}, {nextEvent._start.getDate()} {MESES[nextEvent._start.getMonth()]} · {fmtHour(nextEvent._start)}
                  </div>
                </>
              ) : (
                <div className="text-sm text-neutral-400">Sem próximas sessões.</div>
              )}
            </div>
          </div>

          {/* Lista agrupada por dia */}
          <div className="space-y-6" data-testid="agenda-days-list">
            {groupedByDay.map((g) => {
              const d = g.date;
              const isToday = isSameDay(d, new Date());
              return (
                <div key={dayKey(d)} data-testid={`agenda-day-${dayKey(d)}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={`w-12 h-12 flex flex-col items-center justify-center border ${isToday ? "border-yellow-400 bg-yellow-400/10 text-yellow-400" : "border-neutral-800 text-neutral-400"}`}
                    >
                      <span className="text-[10px] uppercase tracking-widest leading-none">
                        {MESES[d.getMonth()].slice(0, 3)}
                      </span>
                      <span className="text-lg font-display leading-none mt-0.5">
                        {d.getDate()}
                      </span>
                    </div>
                    <div>
                      <div className={`font-display uppercase text-lg tracking-tight ${isToday ? "text-yellow-400" : ""}`}>
                        {DIAS[d.getDay()]}
                      </div>
                      <div className="text-xs text-neutral-500">
                        {g.items.length} {g.items.length === 1 ? "sessão" : "sessões"}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 ml-4 pl-9 border-l border-neutral-900">
                    {g.items.map((ev) => (
                      <div
                        key={`${ev.uid}-${ev._start.toISOString()}`}
                        className="card p-4 hover:border-yellow-400/40 transition-colors"
                        data-testid={`agenda-event`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="text-yellow-400 text-sm font-bold tracking-wider shrink-0 w-16">
                            {ev.all_day ? "—" : fmtHour(ev._start)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold uppercase tracking-tight truncate">
                              {ev.titulo}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-neutral-500">
                              {!ev.all_day && ev.fim ? (
                                <span className="inline-flex items-center gap-1">
                                  <Clock size={12} /> até {fmtHour(new Date(ev.fim))}
                                </span>
                              ) : null}
                              {ev.local ? (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin size={12} /> {ev.local}
                                </span>
                              ) : null}
                            </div>
                            {ev.descricao ? (
                              <div className="text-xs text-neutral-500 mt-2 line-clamp-2">
                                {ev.descricao}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
